import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Container,
  Fade,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider
} from "@mui/material";
import { getUserProfile } from "../../../Services/ClintsApi";
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AddIcon from '@mui/icons-material/Add';
import EmptyStateIcon from '@mui/icons-material/SupportAgent';

const TechnicalSupportPage = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getUserProfile()
      .then((res) => {
        setUserData(res.data);
      })
      .catch(() => {
        setUserData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="error">
          حدث خطأ أثناء تحميل البيانات
        </Typography>
      </Box>
    );
  }

  const { tickets } = userData;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
      case 'مفتوح':
        return 'warning';
      case 'closed':
      case 'مغلق':
        return 'success';
      case 'pending':
      case 'قيد الانتظار':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'مفتوح';
      case 'closed':
        return 'مغلق';
      case 'pending':
        return 'قيد الانتظار';
      default:
        return status || 'غير محدد';
    }
  };

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      py: 4
    }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Fade in={true} timeout={1000}>
          <Box sx={{ 
            textAlign: "center", 
            mb: 6,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "24px",
            p: 5,
            color: "white",
            boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)"
          }}>
            <Avatar 
              sx={{ 
                width: 80, 
                height: 80, 
                mx: "auto", 
                mb: 3,
                background: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(10px)"
              }}
            >
              <SupportAgentIcon sx={{ fontSize: 40, color: "white" }} />
            </Avatar>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: "bold",
                textShadow: "0 2px 10px rgba(0,0,0,0.1)",
                mb: 2
              }}
            >
              الدعم الفني
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                opacity: 0.9,
                fontWeight: 400,
                maxWidth: 600,
                mx: "auto"
              }}
            >
              تابع تذاكر الدعم الفني الخاصة بك واحصل على المساعدة اللازمة
            </Typography>
          </Box>
        </Fade>

        {/* Tickets Summary */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              borderRadius: "20px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)"
            }}>
              <CardContent sx={{ textAlign: "center", p: 4 }}>
                <Avatar sx={{ 
                  bgcolor: "rgba(255,255,255,0.2)", 
                  mx: "auto", 
                  mb: 2,
                  width: 60,
                  height: 60
                }}>
                  <ConfirmationNumberIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                  {tickets.length}
                </Typography>
                <Typography variant="h6">
                  إجمالي التذاكر
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              borderRadius: "20px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              height: "100%"
            }}>
              <CardContent sx={{ textAlign: "center", p: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  sx={{
                    background: "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
                    borderRadius: "16px",
                    py: 2,
                    px: 4,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    boxShadow: "0 8px 25px rgba(76, 175, 80, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #388e3c 0%, #4caf50 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 35px rgba(76, 175, 80, 0.4)"
                    }
                  }}
                >
                  إنشاء تذكرة جديدة
                </Button>
                <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                  هل تحتاج مساعدة؟ أنشئ تذكرة دعم فني جديدة
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tickets List */}
        <Card sx={{ 
          borderRadius: "20px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: "bold", 
                mb: 3,
                color: "#1976d2",
                display: "flex",
                alignItems: "center"
              }}
            >
              <ConfirmationNumberIcon sx={{ mr: 2 }} />
              تذاكر الدعم الفني
            </Typography>
            
            {tickets.length === 0 ? (
              // Empty State
              <Box sx={{ 
                textAlign: "center", 
                py: 8,
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                borderRadius: "16px"
              }}>
                <Avatar sx={{ 
                  bgcolor: "#e3f2fd", 
                  mx: "auto", 
                  mb: 3,
                  width: 80,
                  height: 80
                }}>
                  <EmptyStateIcon sx={{ fontSize: 40, color: "#1976d2" }} />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, color: "#666" }}>
                  لا توجد تذاكر دعم فني
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
                  لم تقم بإنشاء أي تذاكر دعم فني بعد
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "16px",
                    py: 1.5,
                    px: 4,
                    fontSize: "1rem",
                    fontWeight: "bold"
                  }}
                >
                  إنشاء تذكرة جديدة
                </Button>
              </Box>
            ) : (
              // Tickets List
              <List sx={{ p: 0 }}>
                {tickets.map((ticket, index) => (
                  <React.Fragment key={ticket.id || index}>
                    <ListItem sx={{ 
                      p: 3,
                      borderRadius: "16px",
                      mb: 2,
                      background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                      border: "1px solid #e0e0e0",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease"
                      }
                    }}>
                      <Box sx={{ width: "100%" }}>
                        <Box sx={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "flex-start",
                          mb: 2
                        }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                              تذكرة #{ticket.id || index + 1}
                            </Typography>
                            <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
                              {ticket.subject || "موضوع التذكرة"}
                            </Typography>
                          </Box>
                          <Chip 
                            label={getStatusText(ticket.status)}
                            color={getStatusColor(ticket.status)}
                            sx={{ fontWeight: "bold" }}
                          />
                        </Box>
                        
                        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                          {ticket.description || ticket.message || "وصف التذكرة"}
                        </Typography>
                        
                        <Box sx={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          pt: 2,
                          borderTop: "1px solid #e0e0e0"
                        }}>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            تاريخ الإنشاء: {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('ar-EG') : "غير محدد"}
                          </Typography>
                          <Button 
                            variant="outlined" 
                            size="small"
                            sx={{ borderRadius: "8px" }}
                          >
                            عرض التفاصيل
                          </Button>
                        </Box>
                      </Box>
                    </ListItem>
                    {index < tickets.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default TechnicalSupportPage;
